import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { AuthService } from '@auth/services/auth.service';
import { PageHeaderService } from '@ui/app-layout/page-header.service';
import { ButtonComponent } from '@ui/button/button.component';
import { CardComponent } from '@ui/card/card.component';
import { IconComponent } from '@ui/icon/icon.component';
import { InputComponent } from '@ui/input/input.component';
import { TwoFactorModalComponent } from './two-factor-modal/two-factor-modal.component';
import { ModalService } from '@ui/modal/modal.service';
import { UserService } from '@api/services/user.service';
import { User } from '@api/models/user.model';
import { CommonModule } from '@angular/common';

const matchPasswordValidator: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null => {
  const password = control.get('password');
  const confirmPassword = control.get('confirmPassword');
  if (password && confirmPassword && password.value !== confirmPassword.value) {
    confirmPassword.setErrors({ passwordMismatch: true });
    return { passwordMismatch: true };
  }
  confirmPassword?.setErrors({ passwordMismatch: false });
  return { passwordMismatch: false };
};

@Component({
  selector: 'app-user-settings',
  standalone: true,
  imports: [
    CardComponent,
    InputComponent,
    ReactiveFormsModule,
    ButtonComponent,
    CommonModule,
  ],
  template: `
    <ui-card>
      <div class="flex justify-between items-center">
        <h2 class="text-lg font-semibold mb-4">
          {{ this.currentUser()?.name }}
        </h2>
        <!-- @if(this.currentUser()?.two_factor_enabled) {
          <ui-button (click)="disable2Fa()" [variant]="'ghost'">
            <ui-icon name="key"></ui-icon> 2FA uitschakelen
          </ui-button>
        } @else {
          <ui-button (click)="set2Fa()" [variant]="'ghost'">
            <ui-icon name="key"></ui-icon> 2FA instellen
          </ui-button>
        } -->
      </div>
      <form [formGroup]="form" class="flex flex-col gap-4" (ngSubmit)="save()">
        <ui-input id="name" label="Naam" formControlName="name"></ui-input>
        <ui-input
          id="email"
          type="email"
          label="Email"
          formControlName="email"
          autocomplete="new-password"
        ></ui-input>
        <ui-input
          id="phone"
          label="Telefoon"
          formControlName="phone"
        ></ui-input>

        <ui-input
          type="password"
          id="password"
          label="Wachtwoord"
          formControlName="password"
          placeholder="Wachtwoord"
          autocomplete="new-password"
          [ngClass]="{
            'border-red-500':
              form.get('password')?.errors && form.get('password')?.touched
          }"
        />

        <ui-input
          type="password"
          id="confirmPassword"
          label="Bevestig wachtwoord"
          formControlName="password_confirmation"
          placeholder="Bevestig wachtwoord"
          [ngClass]="{
            'border-red-500':
              hasPasswordMismatch() &&
              form.get('password_confirmation')?.touched
          }"
          [error]="
            hasPasswordMismatch() && form.get('password_confirmation')?.touched
              ? 'Wachtwoorden komen niet overeen'
              : undefined
          "
        />

        <ui-button type="submit">Opslaan</ui-button>
      </form>
    </ui-card>
  `,
  styles: `
    :host {
      display: block;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSettingsComponent {
  pageHeader = inject(PageHeaderService);
  authService = inject(AuthService);
  modalService = inject(ModalService);
  userService = inject(UserService);

  currentUser = this.authService.currentUser;

  form = new FormGroup(
    {
      name: new FormControl('', [Validators.required]),
      email: new FormControl({ value: '', disabled: true }, [
        Validators.required,
        Validators.email,
      ]),
      phone: new FormControl('', [Validators.required]),
      password: new FormControl('', []),
      password_confirmation: new FormControl('', []),
    },
    {
      validators: [this.passwordMatchValidator()],
    }
  );

  constructor() {
    effect(() => {
      this.form.patchValue({
        name: this.currentUser()?.name,
        email: this.currentUser()?.email,
        password: '',
        password_confirmation: '',
      });
    });
  }

  ngOnInit() {
    const id = this.currentUser()?.id;
    if (id) {
      this.userService.getUser(id).subscribe((user) => {
        this.form.patchValue({
          name: user.name,
          email: user.email,
          phone: user.phone,
        });
      });
    }
  }

  set2Fa() {
    let modalRef = this.modalService.open(TwoFactorModalComponent);
    modalRef.onClose.subscribe(() => {
      this.authService.getUser().subscribe();
    });
  }

  disable2Fa() {
    this.authService.disableTwoFactor().subscribe(() => {
      this.authService.getUser().subscribe();
    });
  }

  // Custom validator voor wachtwoord matching
  private passwordMatchValidator(): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const password = formGroup.get('password');
      const confirmPassword = formGroup.get('password_confirmation');

      if (password?.value && confirmPassword?.value) {
        if (password.value !== confirmPassword.value) {
          confirmPassword.setErrors({ passwordMismatch: true });
          return { passwordMismatch: true };
        }
      }
      return null;
    };
  }

  // Helper methods voor error messages
  getPasswordErrors(): string[] {
    const control = this.form.get('password');
    if (control?.errors?.['password']) {
      return Array.isArray(control.errors['password'])
        ? control.errors['password']
        : [control.errors['password']];
    }
    return [];
  }

  hasPasswordMismatch(): boolean {
    return (
      this.form.get('password_confirmation')?.errors?.['passwordMismatch'] ===
      true
    );
  }

  save() {
    this.form.markAllAsTouched();
    const id = this.currentUser()?.id;
    if (this.form.valid && id) {
      this.userService.updateUser(id, this.form.value as User).subscribe({
        next: () => {
          this.authService.getUser().subscribe();
        },
        error: (error) => {
          if (error.error.errors) {
            Object.keys(error.error.errors).forEach((key) => {
              const control = this.form.get(key);
              if (control) {
                control.setErrors({ [key]: error.error.errors[key] });
              }
            });
          }
        },
      });
    }
  }
}
